// THE VR LOOK (vr-flat-style, w55, 2026-11-03; APP_VRSTYLE.md): a stylised flat-colour picture for the Quest,
// so the headset runs near its full eye resolution with clean edges. The owner: "desktop and mobile must stay
// as they are, the current vr can go, this is not usable"; "ideally it's new sources with minimal hook into the
// old vr code, so it's kept separate". Every flat-style rule lives under client/src/vrstyle/; the old files
// carry one-line hooks that ask `vrStyleOn()` and call in here.
//
// ON for the Quest class (device.js deviceClass() 'quest', incl. `?dev=quest`) unless `?vrstyle=0` (the old VR
// look, kept reachable until the owner drops it). Every other class: vrStyleOn() = false and no hook changes a
// thing (no program key, no uniform, no object). URL only, never stored: a test URL never sticks to the headset.
//
// The render terms of the style (vrsProfile → questprof.js questProfile, over the picked picture profile):
//   samples   the multiview eye target's MSAA (xrmv.js per frame)             ?msaa=0|2|4   default VRS_DEFAULT
//   res       the viewport scale (adaptres.js band) LOCKED at 1.0             ?res=0.4..1 = another FIXED scale
//   eye       the eye BUFFER's scale (vrsEyeScale → fog.js vrsInit:
//             renderer.xr.setFramebufferScaleFactor before any session)    ?res=1.05..1.5 = supersampled
//   foveation the layer's fixed foveation (fog.js vrsInit)                   ?fov=0..1     default VRS_DEFAULT
//   quality   the projection layer's XRLayerQuality (fog.js, session start)  ?vrq=default|text|graphics
// The picture parts: ground.js (the pool colour alone, softened — no class blend / detail / ways field /
// relight), trees.js (opaque low-poly crowns, no alpha cut-out, no sway), fog.js (aerial fog to the horizon
// colour), buildings.js (each facade tile its flat mean). The far canopy volume, its splats and canopy pass,
// the grass scatter and the shrub sprites are not installed / not drawn (main.js hooks).
import { deviceClass } from '../device.js';

function param(k) {
  try { return new URLSearchParams(globalThis.location ? location.search : '').get(k); } catch { return null; }
}

let _on = null;   // decided once the class is (null before detectDevice)
// cls: the device class (default the decided one) → the flat style is in force
export function vrStyleOn(cls = deviceClass()) {
  if (cls == null) return false;
  if (cls !== deviceClass()) return cls === 'quest' && param('vrstyle') !== '0';
  if (_on == null) _on = cls === 'quest' && param('vrstyle') !== '0';
  return _on;
}

// THE EDGES (w56, APP_VRSTYLE "Edges"): MSAA 4 (Meta: "almost always use MSAA … not go over 4x"; the tiler resolves
// on chip) and the eye buffer at `eye` × the runtime's recommended size (Quest 3: 1.0 = 1680×1760; ~1.2 ≈ the panel's
// own pixels: above 1.0 the compositor's lens-warp resample filters every edge once more — supersampling)
// quality = the projection layer's XRLayerQuality (WebXR Layers; the compositor's own filtering of the eye buffer)
export const VRS_DEFAULT = { samples: 4, res: 1, eye: 1.1, foveation: 0.5, quality: 'default', d: 1500, bg: 0xc9d5df };
const num = (v, lo, hi) => { const x = v == null || v === '' ? NaN : Number(v); return Number.isFinite(x) && x >= lo && x <= hi ? x : null; };

// the style's picture-profile terms (questprof.js shape) or null (style off / not this class)
export function vrsProfile(cls = deviceClass()) {
  if (!vrStyleOn(cls)) return null;
  const ms = num(param('msaa'), 0, 4);
  const samples = ms === 0 || ms === 2 || ms === 4 ? ms : VRS_DEFAULT.samples;
  // the eye resolution is LOCKED (the owner: "with this resolution hack I am not interested"): the style renders
  // less instead (vrsD: the void past D); ?res= ≤ 1 = another fixed scale for the A/B (> 1: vrsEyeScale)
  const r = num(param('res'), 0.4, 1.5), fix = r != null && r <= 1 ? r : VRS_DEFAULT.res;
  const res = { home: fix, home_mv: fix, top: fix, floor: fix };
  return { samples, res, gm_lite: true, vrstyle: true };
}

// the eye BUFFER's scale (× the runtime's recommended size; fog.js vrsInit sets it before any session): ?res= > 1 =
// that supersample, ?res= ≤ 1 = 1 (a viewport scale inside a 1.0 buffer), else the style's; null off the style
export function vrsEyeScale(cls = deviceClass()) {
  if (!vrStyleOn(cls)) return null;
  const r = num(param('res'), 0.4, 1.5);
  return r == null ? VRS_DEFAULT.eye : r > 1 ? r : 1;
}

// the projection layer's quality (fog.js applyVoid at a session's start): ?vrq=default|text|graphics, else the style's
const VRS_Q = { default: 'default', text: 'text-optimized', graphics: 'graphics-optimized' };
export function vrsLayerQuality() {
  if (!vrStyleOn()) return null;
  return VRS_Q[param('vrq')] || VRS_DEFAULT.quality;
}

// the XR layer's fixed foveation: the style's (or ?fov=), else the caller's
export function vrsFoveation(dflt) {
  if (!vrStyleOn()) return dflt;
  const f = num(param('fov'), 0, 1);
  return f != null ? f : VRS_DEFAULT.foveation;
}

// THE VOID: the world is drawn to D m (the camera's far plane: every family past it is culled), fogged to one colour
// (the background) on the way there; ?vrd=<300..20000> / ?vrbg=<rrggbb> for the A/B, __vr.vrstyle({d, bg}) live
let _d = null, _bg = null;
export function vrsD() {
  if (_d == null) _d = num(param('vrd'), 300, 20000) ?? VRS_DEFAULT.d;
  return _d;
}
export function vrsBg() {
  if (_bg == null) { const h = param('vrbg'); _bg = h && /^[0-9a-f]{6}$/i.test(h) ? parseInt(h, 16) : VRS_DEFAULT.bg; }
  return _bg;
}
// a family's draw radius under the style: never past the void (buildings.js cull, aerialway.js chair range, trees)
export function vrsCap(r) { return vrStyleOn() ? Math.min(r, vrsD()) : r; }
export function vrsSetVoid({ d, bg } = {}) {
  if (Number.isFinite(d) && d >= 300 && d <= 20000) _d = d;
  if (Number.isFinite(bg) && bg >= 0 && bg <= 0xffffff) _bg = bg;
}

// the facts (dev: __vr.vrstyle())
export function vrsState() {
  const p = vrsProfile();
  return { on: vrStyleOn(), cls: deviceClass(), profile: p, eye: vrsEyeScale(), quality: vrsLayerQuality(), foveation: vrsFoveation(null), d: vrStyleOn() ? vrsD() : null, bg: vrStyleOn() ? vrsBg().toString(16) : null };
}
// the parts are imported from their own files by their hooks (ground.js, trees.js, fog.js, buildings.js):
// this file stays free of three / the scene modules, so questprof.js (and the node gates) import it alone
